import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  NativeSelect,
  Typography,
} from '@mui/material';
import InputBase from '@mui/material/InputBase';
import { styled } from '@mui/material/styles';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const CompactInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #dde1e6',
    fontSize: 13,
    padding: '6px 10px',
    fontFamily: 'Lato, sans-serif',
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const MasterProcedureDialog = ({
  open,
  onClose,
  onRun,
  running,
  stores,
  masterLabel,
  defaultStartDate,
  defaultEndDate,
  defaultStoreId,
}) => {
  const [storeId, setStoreId] = useState(defaultStoreId || '');
  const [startDate, setStartDate] = useState(defaultStartDate || null);
  const [endDate, setEndDate] = useState(defaultEndDate || null);

  // Reset fields when the dialog opens with new defaults
  React.useEffect(() => {
    if (open) {
      setStoreId(defaultStoreId || '');
      setStartDate(defaultStartDate || null);
      setEndDate(defaultEndDate || null);
    }
  }, [open, defaultStoreId, defaultStartDate, defaultEndDate]);

  const filteredStores = (stores || []).filter((s) => s.storeId !== 'all');
  const canRun = storeId && storeId !== 'all' && startDate && endDate;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Lato', fontWeight: 700 }}>
        {masterLabel || 'Run Master Procedure'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          This will re-ingest raw data for the selected store and re-run all
          downstream transformations for the date range.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Store picker */}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Store
            </Typography>
            <NativeSelect
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              input={<CompactInput />}
              fullWidth
              sx={{ width: '100%' }}
            >
              <option value="" disabled>
                Select a store…
              </option>
              {filteredStores.map(({ storeId: id, storeName, storeType }) => (
                <option key={id} value={id}>
                  {storeName}{storeType ? ` (${storeType})` : ''}
                </option>
              ))}
            </NativeSelect>
          </Box>

          {/* Date range */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Start Date
              </Typography>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="Start"
                className="di2-datepicker"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                End Date
              </Typography>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="End"
                className="di2-datepicker"
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={running}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onRun({ storeId, startDate, endDate })}
          disabled={!canRun || running}
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontFamily: 'Lato' }}
        >
          {running ? 'Running…' : 'Run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MasterProcedureDialog;
